import {
  ExpandableCategoryTablecell,
  PlacesOfDeliveryList,
} from "components/page-components/Ausschreibungsdienste/landingpage-components"
import { Link, graphql, useStaticQuery } from "gatsby"
import React, { useState, useRef, useEffect, createRef } from "react"
import {
  TableHeadingSorter,
  TablePaginationLink,
} from "components/page-components/Ausschreibungsdienste/landingpage-components/utils"

import { Arrow, CaretIcon } from "components/page-components/Icons/small"
import { GatsbyImage } from "gatsby-plugin-image"

export const PaginatedNoticeTable = ({
  Notices,
  location,
  name,
  scroll_top_smooth,
}) => {
  const getDateFromSeconds = (seconds) =>
    new Intl.DateTimeFormat("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(seconds * 1000))
  const slice_size = 50
  const num_slices = Math.min(3, Math.ceil(Notices.length / slice_size))
  const [sort_by, set_sort_by] = useState({ attribute: "", order: true })

  const refs = useRef(Array.from({ length: num_slices }).map(() => createRef()))

  const shown_notices = !sort_by.attribute
    ? Notices
    : [...Notices].sort((a, b) =>
        sort_by.attribute === "POD"
          ? sort_by.order
            ? a.places_of_delivery_struct[0].PostalCode -
              b.places_of_delivery_struct[0].PostalCode
            : b.places_of_delivery_struct[0].PostalCode -
              a.places_of_delivery_struct[0].PostalCode
          : typeof a[sort_by.attribute] === "string"
          ? sort_by.order
            ? a[sort_by.attribute].localeCompare(b[sort_by.attribute])
            : b[sort_by.attribute].localeCompare(a[sort_by.attribute])
          : sort_by.order
          ? a[sort_by.attribute] - b[sort_by.attribute]
          : b[sort_by.attribute] - a[sort_by.attribute]
      )

  const [current_notice_slice, set_current_notice_slice] = useState(0)
  const [current_notice_ref, set_current_notice_ref] = useState(null)
  const go_to_prev_slice = (e) => {
    e.preventDefault()
    if (current_notice_slice === 0) {
      return
    }
    scroll_top_smooth()
    set_current_notice_slice(
      current_notice_slice !== "cta" ? current_notice_slice - 1 : num_slices - 1
    )
  }
  const go_to_next_slice = (e) => {
    e.preventDefault()
    if (current_notice_slice === "cta") {
      return
    }
    scroll_top_smooth()
    set_current_notice_slice(
      current_notice_slice !== num_slices - 1 ? current_notice_slice + 1 : "cta"
    )
  }
  useEffect(() => {
    set_current_notice_ref(refs.current[current_notice_slice])
  }, [current_notice_slice])

  const go_to_last_slice = () => set_current_notice_slice("cta")

  const { lookingForTenders } = useStaticQuery(graphql`
    {
      lookingForTenders: file(name: { eq: "looking-for-tenders" }) {
        childImageSharp {
          gatsbyImageData(
            placeholder: BLURRED
            layout: CONSTRAINED
            formats: [AUTO, AVIF]
            width: 300
          )
        }
      }
    }
  `)

  const sort_by_attribute = (chosen_attribute) => {
    return chosen_attribute === sort_by.attribute
      ? set_sort_by({ attribute: chosen_attribute, order: !sort_by.order })
      : set_sort_by({ attribute: chosen_attribute, order: false })
  }

  return (
    <React.Fragment>
      {current_notice_slice === "cta" ? (
        <div className="columns is-vcentered is-centered my-6">
          <div className="column is-3 has-text-centered">
            <GatsbyImage
              alt="Auf der Suche nach Aufträgen?"
              title="Auf der Suche nach Aufträgen?"
              image={lookingForTenders.childImageSharp.gatsbyImageData}
            />
          </div>
          <div className="column is-7">
            <p className="has-text-mitternachtsblau mb-5">
              Nichts interessantes dabei?
            </p>
            <Link
              to={`/ausschreibungsdienste#bundesland=${name}`}
              className="title has-text-mitternachtsblau is-4 link"
            >
              Jetzt weitere Aufträge in {name} und ganz Deutschland finden.
            </Link>
            <p className="has-text-mitternachtsblau mt-5">
              Hier finden Sie Ausschreibungen für alle Gewerke und Branchen in
              allen Bundesländern und Regionen.
            </p>
          </div>
          <div className="column is-narrow">
            <Link to={`/ausschreibungsdienste#bundesland=${name}`}>
              <Arrow {...{ direction: "right", size: 1 }} />
            </Link>
          </div>
        </div>
      ) : (
        <div
          className="is-clipped"
          style={{
            transition: "all .3s ease .3s",
            display: "grid",
            maxHeight: !!current_notice_ref
              ? current_notice_ref?.current?.clientHeight + 100
              : undefined,
          }}
        >
          {Array.from({ length: num_slices }).map((_, index) => {
            const notice_slice = shown_notices.slice(
              slice_size * index + num_slices,
              slice_size * index + slice_size + num_slices
            )
            return (
              <div
                key={`notice-table-${index}`}
                className=""
                style={{
                  transition: "all .3s ease",
                  gridArea: "1/1",
                  position: "relative",
                  opacity: current_notice_slice === index ? "1" : "0",
                  visibility:
                    current_notice_slice === index ? "visible" : "hidden",
                  transform: `translateX(${
                    -30 * (current_notice_slice - index)
                  }%)`,
                }}
              >
                <div
                  ref={refs.current[index]}
                  className="table-container mt-6"
                  id="paginated-table-top"
                >
                  <table
                    className="table is-fullwidth is-striped is-hoverable has-background-mitternachtsblau-5"
                    style={{
                      // this gets ignored, but is important for table cells in expandable cells component
                      height: 1,
                    }}
                  >
                    <colgroup>
                      <col width="110px" />
                      <col width="765px" />
                      <col width="150px" />
                      <col />
                    </colgroup>
                    <thead>
                      <tr>
                        <TableHeadingSorter
                          {...{
                            attribute: "ActBefore",
                            heading: "Frist",
                            set_current_notice_slice,
                            sort_by_attribute,
                            sort_by,
                          }}
                        />
                        <TableHeadingSorter
                          {...{
                            attribute: "TitelOnline",
                            heading: "Titel",
                            set_current_notice_slice,
                            sort_by_attribute,
                            sort_by,
                          }}
                        />
                        <TableHeadingSorter
                          {...{
                            attribute: "ServiceType",
                            heading: "Leistungen",
                            set_current_notice_slice,
                            sort_by_attribute,
                            sort_by,
                          }}
                        />
                        <TableHeadingSorter
                          {...{
                            attribute: "POD",
                            heading: "Ort",
                            set_current_notice_slice,
                            sort_by_attribute,
                            sort_by,
                          }}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {notice_slice.map(
                        (
                          {
                            ActBefore,
                            top_three_categories,
                            NoticeTypeName,
                            RefIDStringBI,
                            places_of_delivery_struct,
                            ServiceTypeName,
                            ZoneOfRequest,
                            TitelOnline,
                          },
                          i
                        ) => {
                          const tender_date = ActBefore
                            ? getDateFromSeconds(ActBefore)
                            : null
                          return (
                            <tr
                              key={`demo-tender-${i}`}
                              style={{
                                height: "auto",
                              }}
                            >
                              <td
                                className="has-text-mitternachtsblau"
                                dangerouslySetInnerHTML={{
                                  __html: tender_date || "k.&#x202F;A.",
                                }}
                                aria-label="table-field"
                              ></td>
                              <ExpandableCategoryTablecell
                                {...{
                                  RefIDStringBI,
                                  TitelOnline,
                                  top_three_categories,
                                  main_pod:
                                    places_of_delivery_struct?.[0]?.City,
                                }}
                              />
                              <td className="has-text-mitternachtsblau">
                                {ZoneOfRequest === 4
                                  ? "privater Bauauftrag"
                                  : ServiceTypeName}
                              </td>
                              <td
                                className="has-text-mitternachtsblau"
                                style={{ minWidth: 230 }}
                              >
                                <PlacesOfDeliveryList
                                  {...{
                                    places_of_delivery_struct,
                                    location,
                                  }}
                                />
                              </td>
                            </tr>
                          )
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <nav
        className="pagination is-centered is-small mt-5"
        role="navigation"
        aria-label="pagination"
      >
        <ul className="pagination-list">
          <li>
            <a
              onClick={go_to_prev_slice}
              className={`pagination-link `}
              href="#top"
              aria-label="Vor-Vorherige Seite"
            >
              <CaretIcon
                {...{
                  size: 0.8,
                  color: current_notice_slice === 0 ? "#7aa0ad" : "#034055",
                  rotation: "left",
                }}
              />
            </a>
          </li>
          {Array.from({ length: num_slices }).map((_, i) => (
            <TablePaginationLink
              key={`pag-${i}`}
              {...{
                scroll_top_smooth,
                current_notice_slice,
                set_current_notice_slice,
                i,
              }}
            />
          ))}
          <li>
            <a
              onClick={go_to_last_slice}
              className={`pagination-link ${
                current_notice_slice === "cta" ? "is-current" : ""
              }`}
              href="#top"
              aria-label="Vor-Vorherige Seite"
            >
              {num_slices + 1}
            </a>
          </li>
          <li>
            <a
              onClick={go_to_next_slice}
              className={`pagination-link `}
              href="#top"
              aria-label="Vor-Vorherige Seite"
            >
              <CaretIcon
                {...{
                  size: 0.8,
                  color: current_notice_slice === "cta" ? "#7aa0ad" : "#034055",
                  rotation: "right",
                }}
              />
            </a>
          </li>
        </ul>
      </nav>
    </React.Fragment>
  )
}
